import { AgTechDataAction } from "@agtech/core/data/actions/AgTechDataActions";
import { useAgTechHttpPostRequest } from "@agtech/core/data/http/AgTechApiPostRequests";
import { invalidateInvoiceQueries, InvoiceDataGetUnprocessedSalesQuery } from "../../invoicing/InvoicingDataQueries";
import { useCurrentSale } from "../../sales/SaleDataStore";
import { SaleContactDataGetContactDetailsQuery } from "../../operation/contacts/SaleContactDataQueries";
import { LotDataGetLotsQuery } from "../LotDataQueries";
import { InvoiceDataGetInvoiceLotsQuery } from "./LotInvoiceDataQueries";

/// DTOs

export type ChangeItemOnInvoiceDTO = {
    remove: boolean,
    id: number
}

/// Actions

export const useLotDataWriteLotInvoiceChangesAction = (inputData: {
    invoiceId: number,
    contactId: number
}): AgTechDataAction<ChangeItemOnInvoiceDTO[], boolean> => {
    const currentSale = useCurrentSale();

    return {
        name: 'WriteLotChanges',
        getConfiguration: props => ({
            actionExecutionMessage: 'Updating lots...',
            actionConfirmationMessage: 'Successfully updated lots'
        }),
        validate: async props => {
            // TODO: Add validations to prevent removal of all lots on an invoice
        }, 
        action: async props => {
            return await props.http.post<ChangeItemOnInvoiceDTO[], boolean>({
                request: `Invoice/${inputData.invoiceId}/ChangeLots`,
                inputData: props.submittedEntity,
            });
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, { 
                saleId: currentSale.id,
                invoiceId: inputData.invoiceId
            });

            props.invalidateQuery({
                query: LotDataGetLotsQuery,
                queryContext: { saleId: currentSale.id }
            });

            props.invalidateQuery({
                query: InvoiceDataGetUnprocessedSalesQuery,
                queryContext: { saleId: currentSale.id }
            });

            props.invalidateQuery({
                query: InvoiceDataGetInvoiceLotsQuery,
                queryContext: {
                    saleId: currentSale.id,
                    invoiceId: inputData.invoiceId,
                    contactId: inputData.contactId
                }
            });
        }
    }
}

export const useDiscountDataWriteDiscountInvoiceChangesAction = (inputData: {
    invoiceId: number,
    contactId: number
}): AgTechDataAction<ChangeItemOnInvoiceDTO[], boolean> => {
    const currentSale = useCurrentSale();

    return {
        name: 'WriteDiscountChanges',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating discounts...',
            actionConfirmationMessage: 'Successfully updated discounts'
        }),
        action: async props => {
            return await props.http.post<ChangeItemOnInvoiceDTO[], boolean>({
                request: `Invoice/${inputData.invoiceId}/ChangeDiscounts`,
                inputData: props.submittedEntity,
            });
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, { 
                saleId: currentSale.id,
                invoiceId: inputData.invoiceId
            });

            props.invalidateQuery({
                query: SaleContactDataGetContactDetailsQuery,
                queryContext: { contactId: inputData.contactId }
            });
        }
    }
}

export const useCreditsDataWriteCreditInvoiceChangesAction = (inputData: {
    invoiceId: number,
    contactId: number
}): AgTechDataAction<ChangeItemOnInvoiceDTO[], boolean> => {
    const currentSale = useCurrentSale();

    return {
        name: 'WriteCreditChanges',
        getConfiguration: () => ({
            actionExecutionMessage: 'Updating credits...',
            actionConfirmationMessage: 'Successfully updated credits'
        }),
        action: async props => {
            return await props.http.post<ChangeItemOnInvoiceDTO[], boolean>({
                request: `Invoice/${inputData.invoiceId}/ChangeCredits`,
                inputData: props.submittedEntity,
            });
        },
        onSuccess: async props => {
            invalidateInvoiceQueries(props, { 
                saleId: currentSale.id,
                invoiceId: inputData.invoiceId
            });

            props.invalidateQuery({
                query: SaleContactDataGetContactDetailsQuery,
                queryContext: { contactId: inputData.contactId }
            });
        }
    }
}